import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from "./context/DomainContext";
import { getOfficesByCompany, getProviderLoginMappings, configureProviderLogin, getUsersBySearch } from './services/masterDataService';

const BRAND_GREEN = '#0aa375';

const ConfigureProviderLogin = () => {
    const { domain } = useCurrentDomain();

    const allowedDomains = ['greenyourbills.com', 'ab.greenyourbills.com'];
    const hasAccess = allowedDomains.includes(domain?.toLowerCase() || '');

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedOfficeIds, setSelectedOfficeIds] = useState([]);
    const [officeSearch, setOfficeSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    const [mappings, setMappings] = useState([]);
    const [mappingLoading, setMappingLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [tableSearch, setTableSearch] = useState('');

    const { companies, loading: companiesLoading } = useCompanies(domain);
    const [offices, setOffices] = useState([]);
    const [officesLoading, setOfficesLoading] = useState(false);

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '32px',
            fontSize: '12px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: BRAND_GREEN },
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '12px',
            backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
            color: state.isSelected ? 'white' : '#333',
        }),
        singleValue: (base) => ({ ...base, fontSize: '12px' }),
        placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
    };

    const Field = ({ label, required }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {label}
                {required && <span style={{ color: '#dc3545', marginLeft: '3px' }}>*</span>}
            </span>
        </div>
    );

    const Spinner = () => (
        <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status" />
        </div>
    );

    const mappedOfficeIds = new Set(mappings.map(m => m.providerID?.trim()).filter(Boolean));

    const availableOffices = offices.filter(o => !mappedOfficeIds.has(o.id));

    const filteredOffices = availableOffices.filter(o =>
        (o.text || o.officeName || '').toLowerCase().includes(officeSearch.toLowerCase())
    );

    const filteredMappings = mappings.filter(item => {
        if (!tableSearch) return true;
        const term = tableSearch.toLowerCase();
        return (
            (item.companyName || '').toLowerCase().includes(term) ||
            (item.userName || '').toLowerCase().includes(term) ||
            (item.userID || '').toLowerCase().includes(term) ||
            (item.providerName || '').toLowerCase().includes(term) ||
            (item.providerID || '').toLowerCase().includes(term)
        );
    });

    useEffect(() => {
        if (!selectedCompany?.value) {
            setOffices([]);
            setMappings([]);
            return;
        }
        const loadData = async () => {
            setOfficesLoading(true);
            setMappingLoading(true);
            try {
                const [officesRes, mappingsRes] = await Promise.all([
                    getOfficesByCompany(domain, selectedCompany.value),
                    getProviderLoginMappings(domain, selectedCompany.value)
                ]);
                setOffices(officesRes);
                setMappings(mappingsRes);
            } catch (err) {
                toast.error('Failed to load data');
            } finally {
                setOfficesLoading(false);
                setMappingLoading(false);
            }
        };
        loadData();
    }, [selectedCompany, domain]);

    const loadUserOptions = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];
        try {
            const users = await getUsersBySearch(domain, inputValue.trim());
            return users.map(u => ({
                value: u.userId,
                label: u.userName || u.userId
            }));
        } catch (err) {
            toast.error('Failed to search users');
            return [];
        }
    };

    const handleCompanyChange = (selected) => {
        setSelectedCompany(selected);
        setSelectedOfficeIds([]);
        setOfficeSearch('');
        setSelectedUser(null);
    };

    const handleConfigure = async () => {
        if (!selectedCompany || !selectedUser || selectedOfficeIds.length === 0) {
            toast.warning("Please select Company, User and at least one Office");
            return;
        }

        setAddLoading(true);
        const loadingToast = toast.loading('Assigning Provider...');

        try {
            const response = await configureProviderLogin({
                companyId: selectedCompany.value,
                userId: selectedUser.value,
                officeIds: selectedOfficeIds,
                domain: domain
            });

            toast.dismiss(loadingToast);
            toast.success(response.message || "Provider configured successfully.");

            setSelectedOfficeIds([]);
            setOfficeSearch('');
            const freshMappings = await getProviderLoginMappings(domain, selectedCompany.value);
            setMappings(freshMappings);
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to configure provider.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleReset = () => {
        setSelectedCompany(null);
        setSelectedUser(null);
        setSelectedOfficeIds([]);
        setOfficeSearch('');
        setMappings([]);
    };

    if (!hasAccess) {
        return (
            <div className="container-fluid p-4">
                <div className="alert alert-danger text-center py-4">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    The selected domain doesn't have Configure Provider Login feature.
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0 px-1">
            <style>{`
                .tbl-compact thead th { 
                    background: #f8f9fa; 
                    font-size: 11.5px; 
                    font-weight: 600; 
                    color: #495057; 
                    text-transform: uppercase; 
                    letter-spacing: 0.3px; 
                    white-space: nowrap;
                    border-bottom: 2px solid #dee2e6 !important;
                    padding: 10px 8px !important;
                }
                .tbl-compact td { 
                    padding: 9px 8px !important;
                    vertical-align: middle; 
                    border: none !important;
                }
                .tbl-compact tbody tr { 
                    height: 46px; 
                    border-bottom: 1px solid #f1f1f1 !important;
                }
                .tbl-compact tbody tr:last-child { 
                    border-bottom: none !important;
                }
                .tbl-compact tbody tr:hover { 
                    background-color: #f0fdf8; 
                }
            `}</style>

            <div className="card-body">
                <div className="row g-3">

                    <div className="col-12 col-lg-6">
                        <div className="card border" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ backgroundColor: BRAND_GREEN, color: '#fff', fontWeight: 600, fontSize: '14px', padding: '9px 12px' }}>
                                Configure Provider Login
                            </div>
                            <div className="card-body p-3" style={{ minHeight: '380px' }}>
                                <div className="mb-3">
                                    <Field label="Company" required />
                                    <Select
                                        options={companies}
                                        value={selectedCompany}
                                        onChange={handleCompanyChange}
                                        isLoading={companiesLoading}
                                        placeholder="Search and select company..."
                                        isSearchable
                                        isClearable
                                        styles={selectStyles}
                                    />
                                </div>

                                <div className="mb-2">
                                    <Field label="Search Providers" />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Filter available providers..."
                                        value={officeSearch}
                                        onChange={(e) => setOfficeSearch(e.target.value)}
                                        disabled={!selectedCompany}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>

                                <div style={{ height: '240px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {officesLoading ? <Spinner /> : !selectedCompany ? (
                                        <div className="text-center py-5 text-muted" style={{ fontSize: '13px' }}>
                                            Select a company first
                                        </div>
                                    ) : filteredOffices.length === 0 ? (
                                        <div className="text-center py-5 text-muted" style={{ fontSize: '13px' }}>
                                            No available Provider found
                                        </div>
                                    ) : (
                                        filteredOffices.map((o, idx) => (
                                            <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12.5px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedOfficeIds.includes(o.id)}
                                                    onChange={() => setSelectedOfficeIds(prev =>
                                                        prev.includes(o.id) ? prev.filter(id => id !== o.id) : [...prev, o.id]
                                                    )}
                                                    style={{ accentColor: BRAND_GREEN }}
                                                />
                                                <span>{o.text || o.officeName}</span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-6">
                        <div className="card border" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={{ backgroundColor: BRAND_GREEN, color: '#fff', fontWeight: 600, fontSize: '14px', padding: '9px 12px' }}>
                                Select User
                            </div>
                            <div className="card-body p-3" style={{ minHeight: '380px' }}>
                                <div className="mb-3">
                                    <Field label="User" required />
                                    <AsyncSelect
                                        cacheOptions
                                        defaultOptions={false}
                                        loadOptions={loadUserOptions}
                                        value={selectedUser}
                                        onChange={setSelectedUser}
                                        placeholder="Search user (min 2 charcters)"
                                        isClearable
                                        styles={selectStyles}
                                        isDisabled={!selectedCompany}
                                        loadingMessage={() => "Searching..."}
                                        noOptionsMessage={() => "No users found"}
                                    />
                                </div>

                                <div className="d-flex justify-content-end gap-2 mb-3">
                                    <button
                                        onClick={handleReset}
                                        className="btn btn-outline-secondary btn-sm px-4"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleConfigure}
                                        disabled={addLoading || !selectedCompany || !selectedUser || selectedOfficeIds.length === 0}
                                        className="btn btn-sm text-white px-4 fw-semibold"
                                        style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px' }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Configuring...
                                            </>
                                        ) : 'Configure'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>
                            Configured Mappings for Selected Company 
                            <span style={{ color: '#0aa375', fontWeight: 700 }}>
                                {mappings.length > 0 && ` (${mappings.length} total)`}
                            </span>
                        </span>

                        <div style={{ width: '300px', marginLeft: 'auto', marginRight: '15px' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search in table..."
                                value={tableSearch}
                                onChange={(e) => setTableSearch(e.target.value)}
                                style={{ fontSize: '12px' }}
                            />
                        </div>

                        {selectedCompany && (
                            <span style={{ fontSize: '12px', color: '#6c757d' }}>
                                Company: {selectedCompany.label}
                            </span>
                        )}
                    </div>

                    <div className="table-responsive" style={{ maxHeight: '520px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                        <table className="table table-hover mb-0 tbl-compact" style={{ fontSize: '12px', tableLayout: 'fixed' }}>
                            <thead className="sticky-top">
                                <tr>
                                    <th style={{ width: '20%' }}>Company Name</th>
                                    <th style={{ width: '18%' }}>User Name</th>
                                    <th style={{ width: '15%' }}>User ID</th>
                                    <th style={{ width: '22%' }}>Provider Name</th>
                                    <th style={{ width: '15%' }}>Provider ID</th>
                                    <th style={{ width: '10%' }}>Mapped Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mappingLoading ? (
                                    <tr><td colSpan="6" className="text-center py-5"><Spinner /></td></tr>
                                ) : filteredMappings.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted">
                                            {selectedCompany ? 'No matching records found' : 'Select a company to view mappings'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredMappings.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{item.companyName}</td>
                                            <td>{item.userName}</td>
                                            <td>{item.userID}</td>
                                            <td>{item.providerName}</td>
                                            <td>{item.providerID}</td>
                                            <td>{item.mappedDate ? new Date(item.mappedDate).toLocaleString() : '-'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigureProviderLogin;